import React from 'react';
import { Eye } from 'lucide-react';

/**
 * ProviderCredentialForm — what each connector provider needs, in ONE place,
 * rendered the same way by every surface that onboards one.
 *
 * WHY THIS IS SHARED AND NOT A SETTINGS DETAIL
 * --------------------------------------------
 * This table started inside `Settings/SettingsPage.tsx` on 2026-08-09, after
 * closing the tile grid made every tile POST for real and the probes started
 * refusing honestly — K8S_MISSING_CREDENTIALS, REGISTRY_MISSING_URL,
 * SCM_MISSING_TOKEN, EXTRA_PROVIDER_MISSING_CREDENTIALS — with nowhere in the
 * wizard to type the credential being asked for.
 *
 * THERE WAS A SECOND ONBOARDING SURFACE AND IT WAS BROKEN OUTRIGHT.
 * `Onboarding/OnboardingWizard.tsx` — the first-run wizard, the first thing a
 * new tenant sees — carried its own three-provider field list and POSTed
 * `{provider, name, credentials}` with **no external_id at all**. Measured
 * 2026-08-09 against the live gateway: aws, azure and gcp each answered
 * **HTTP 400 `external_id required for provider '<p>'`**. Not a degraded path —
 * the first-run wizard could not connect any cloud account whatsoever, and had
 * not been able to since the external_id requirement went catalog-wide on
 * 2026-08-01. Its credential fields were wrong too: AWS collected a role ARN
 * with no External ID (which create refuses as a confused-deputy risk) and no
 * access keys; Azure collected a tenant id with no client id or secret; GCP
 * collected a service-account *email*, which authenticates nothing.
 *
 * Two surfaces, two field lists, one backend contract is the exact shape this
 * repo keeps paying for. So the field lists live here, both surfaces render
 * them with the same component, and one gate arm checks them against
 * `services/core/cloud-connector/app/provider_catalog.py`.
 *
 * EACH FIELD `id` IS THE EXACT KEY THE BACKEND PROBE READS, so this table
 * doubles as the record of what each provider needs:
 *
 *   aws                connectors_crud.test_connection_standalone (STS) and
 *                      connector_lifecycle.test_connector's aws branch
 *   azure / gcp        the same two, via ClientSecretCredential / service_account
 *   kubernetes         kubeconfig | bearer_token + cluster_endpoint
 *   container_registry the registry branch incl. _REGISTRY_TYPE_REQUIRES shapes
 *   source_repository  the SCM branch (GitHub / GitLab / Bitbucket)
 *   cloudflare/vercel/ connectors_crud._EXTRA_PROVIDER_CRED_KEYS
 *   linode/vmware
 *   oci / alibaba      shared.collectors.provider_fleet.{resolve_oci_config,
 *                      resolve_alibaba_credentials}
 *
 * `externalIdField` must equal the catalog spec's `external_id_field`: create
 * refuses a blank external_id (cloud_accounts carries a CHECK), and for most of
 * these providers the identity IS a credential input — the registry URL, the
 * vCenter host, the SCM org. Arm P7 of check_connector_provider_parity.py
 * asserts every creatable provider appears here and that the two agree.
 */

export type CredFieldKind = 'text' | 'secret' | 'textarea' | 'select';

export interface CredField {
  /** The exact key the backend probe reads. Not a display id. */
  id: string;
  label: string;
  kind?: CredFieldKind;
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  help?: string;
  /** Render only when this holds. Used for auth-shape switches. */
  when?: (v: Record<string, string>) => boolean;
  /** A discriminator the form needs and the backend does not — never sent. */
  uiOnly?: boolean;
  rows?: number;
}

export interface CredentialForm {
  /** MUST equal ProviderSpec.external_id_field in provider_catalog.py. */
  externalIdField: string;
  note?: string;
  fields: CredField[];
  /** Tile id -> field values it fixes (the ECR tile IS registry_type 'ecr'). */
  presetForTile?: Record<string, Record<string, string>>;
  /**
   * Settings renders a RICHER panel for this provider (CloudFormation template,
   * deploy-method selector, per-scope layout) instead of the generic renderer.
   * The fields above are still the one definition of what the provider needs —
   * this only says a second, fuller rendering of them exists and where.
   */
  richerFormAt?: string;
}

export const K8S_AUTH = 'k8s_auth_mode';
export const AWS_AUTH = 'aws_auth_mode';
const REGISTRY_BASIC_TYPES = ['docker', 'harbor', 'quay', 'generic'];

export const PROVIDER_CREDENTIAL_FORMS: Record<string, CredentialForm> = {
  aws: {
    externalIdField: 'account_id',
    richerFormAt: 'Settings > Add Deployment (adds the CloudFormation template, the deploy-method selector and org-scope onboarding)',
    note: 'Read-only access. The probe calls sts:GetCallerIdentity and refuses if the credentials belong to a different account than the one claimed.',
    fields: [
      { id: 'account_id', label: 'AWS account ID', required: true, placeholder: '123456789012',
        help: 'The 12-digit account number. The probe compares it against the account the credentials actually authenticate to.' },
      { id: AWS_AUTH, label: 'Authentication', kind: 'select', uiOnly: true,
        options: [
          { value: 'access_keys', label: 'IAM access keys' },
          { value: 'assume_role', label: 'Cross-account IAM role (AssumeRole)' },
        ] },
      { id: 'aws_access_key_id', label: 'Access key ID', required: true,
        when: v => (v[AWS_AUTH] || 'access_keys') === 'access_keys',
        placeholder: 'AKIAIOSFODNN7EXAMPLE' },
      { id: 'aws_secret_access_key', label: 'Secret access key', kind: 'secret', required: true,
        when: v => (v[AWS_AUTH] || 'access_keys') === 'access_keys' },
      { id: 'role_arn', label: 'IAM role ARN', required: true,
        when: v => v[AWS_AUTH] === 'assume_role',
        placeholder: 'arn:aws:iam::123456789012:role/CyberShieldReadOnly' },
      { id: 'aws_external_id', label: 'External ID', kind: 'secret', required: true,
        when: v => v[AWS_AUTH] === 'assume_role',
        help: 'The sts:ExternalId in the role\'s trust policy. Required with a role ARN — create refuses the pair without it, because a role trusted without one is a confused-deputy risk.' },
    ],
  },

  azure: {
    externalIdField: 'subscription_id',
    richerFormAt: 'Settings > Add Deployment (adds management-group bulk onboarding)',
    note: 'An app registration with the Reader role on the subscription. Probed with ClientSecretCredential + a resource-group list.',
    fields: [
      { id: 'subscription_id', label: 'Subscription ID', required: true,
        placeholder: '00000000-0000-0000-0000-000000000000' },
      { id: 'tenant_id', label: 'Directory (tenant) ID', required: true,
        placeholder: '00000000-0000-0000-0000-000000000000' },
      { id: 'client_id', label: 'Application (client) ID', required: true,
        placeholder: '00000000-0000-0000-0000-000000000000' },
      { id: 'client_secret', label: 'Client secret', kind: 'secret', required: true,
        help: 'The secret VALUE, not its ID.' },
    ],
  },

  gcp: {
    externalIdField: 'project_id',
    richerFormAt: 'Settings > Add Deployment (adds organization/folder bulk onboarding)',
    note: 'A service-account JSON key with Security Reviewer + Cloud Asset Viewer. The service-account EMAIL alone authenticates nothing — the key file is what the probe needs.',
    fields: [
      { id: 'project_id', label: 'Project ID', required: true, placeholder: 'my-gcp-project-123' },
      { id: 'service_account_json', label: 'Service account JSON key', kind: 'textarea', rows: 8,
        required: true, placeholder: '{"type":"service_account", …}',
        help: 'Paste the whole key file. It is parsed and forwarded server-side, never logged.' },
    ],
  },

  kubernetes: {
    externalIdField: 'cluster_id',
    note: 'One connector, one credential, one collector — EKS, AKS, GKE and self-managed clusters are all reached with a kubeconfig or a service-account token against the Kubernetes API. Read-only is enough: the probe lists namespaces.',
    fields: [
      { id: 'cluster_id', label: 'Cluster identifier', required: true,
        placeholder: 'prod-eks-use1',
        help: 'How this cluster is named in the platform. The kube-system namespace UID is the stable choice; any name unique to this tenant works.' },
      { id: K8S_AUTH, label: 'Authentication', kind: 'select', uiOnly: true,
        options: [
          { value: 'kubeconfig', label: 'kubeconfig YAML' },
          { value: 'token', label: 'Service-account token + API server endpoint' },
        ] },
      { id: 'kubeconfig', label: 'kubeconfig', kind: 'textarea', rows: 9, required: true,
        when: v => (v[K8S_AUTH] || 'kubeconfig') === 'kubeconfig',
        placeholder: 'apiVersion: v1\nkind: Config\nclusters:\n  - name: …',
        help: 'Pasted as-is and forwarded server-side. The current-context cluster is the one probed.' },
      { id: 'cluster_endpoint', label: 'API server endpoint', required: true,
        when: v => v[K8S_AUTH] === 'token',
        placeholder: 'https://api.my-cluster.example.com' },
      { id: 'bearer_token', label: 'Service-account token', kind: 'secret', required: true,
        when: v => v[K8S_AUTH] === 'token',
        help: 'A ServiceAccount token bound to a read-only ClusterRole (pods/nodes/namespaces list+get).' },
    ],
  },

  container_registry: {
    externalIdField: 'registry_url',
    note: 'The probe authenticates against the registry\'s /v2/ endpoint. ECR, GCR and ACR do not take a username and password — they take cloud credentials and exchange them for a registry token, so the fields change with the type.',
    presetForTile: {
      ecr: { registry_type: 'ecr' },
      gcr: { registry_type: 'gcr' },
      acr: { registry_type: 'acr' },
      dockerhub: { registry_type: 'docker', registry_url: 'https://index.docker.io' },
    },
    fields: [
      { id: 'registry_url', label: 'Registry URL', required: true,
        placeholder: '123456789012.dkr.ecr.us-east-1.amazonaws.com',
        help: 'Scheme optional — https:// is assumed.' },
      { id: 'registry_type', label: 'Registry type', kind: 'select',
        options: [
          { value: 'docker', label: 'Docker Hub / Docker Registry (Basic auth)' },
          { value: 'harbor', label: 'Harbor (Basic auth)' },
          { value: 'quay', label: 'Quay (Basic auth)' },
          { value: 'generic', label: 'Generic OCI registry (Basic auth)' },
          { value: 'ecr', label: 'Amazon ECR (AWS token exchange)' },
          { value: 'gcr', label: 'Google GCR / Artifact Registry (SA token exchange)' },
          { value: 'acr', label: 'Azure ACR (service-principal token exchange)' },
        ] },
      { id: 'username', label: 'Username', required: true,
        when: v => REGISTRY_BASIC_TYPES.includes(v.registry_type || 'docker') },
      { id: 'password', label: 'Password / access token', kind: 'secret', required: true,
        when: v => REGISTRY_BASIC_TYPES.includes(v.registry_type || 'docker') },
      { id: 'aws_access_key_id', label: 'AWS access key ID', required: true,
        when: v => v.registry_type === 'ecr', placeholder: 'AKIAIOSFODNN7EXAMPLE',
        help: 'Needs ecr:GetAuthorizationToken. The probe exchanges it for a registry token; nothing is written to your account.' },
      { id: 'aws_secret_access_key', label: 'AWS secret access key', kind: 'secret', required: true,
        when: v => v.registry_type === 'ecr' },
      { id: 'region', label: 'ECR region', when: v => v.registry_type === 'ecr',
        placeholder: 'us-east-1', help: 'Defaults to us-east-1 when blank.' },
      { id: 'gcp_service_account_key', label: 'Service account JSON key', kind: 'textarea', rows: 7,
        required: true, when: v => v.registry_type === 'gcr',
        placeholder: '{"type":"service_account", …}' },
      { id: 'azure_tenant_id', label: 'Azure tenant ID', required: true,
        when: v => v.registry_type === 'acr', placeholder: '00000000-0000-0000-0000-000000000000' },
      { id: 'azure_client_id', label: 'Azure client ID', required: true,
        when: v => v.registry_type === 'acr', placeholder: '00000000-0000-0000-0000-000000000000' },
      { id: 'azure_client_secret', label: 'Azure client secret', kind: 'secret', required: true,
        when: v => v.registry_type === 'acr' },
    ],
  },

  source_repository: {
    externalIdField: 'organization',
    note: 'A read-only token is enough. The probe calls the identity endpoint (/user) and then lists up to five repositories, so a token that authenticates but cannot see the org fails here rather than at the first scan.',
    presetForTile: { github: { scm_type: 'github' }, gitlab: { scm_type: 'gitlab' } },
    fields: [
      { id: 'scm_type', label: 'Provider', kind: 'select',
        options: [
          { value: 'github', label: 'GitHub' },
          { value: 'gitlab', label: 'GitLab' },
          { value: 'bitbucket', label: 'Bitbucket' },
        ] },
      { id: 'organization', label: 'Organization / group / workspace', required: true,
        placeholder: 'my-org',
        help: 'GitHub org, GitLab group or Bitbucket workspace. Also the connector\'s identity in the platform.' },
      { id: 'token', label: 'Personal access token', kind: 'secret', required: true,
        help: 'GitHub: Contents:Read. GitLab: read_repository / read_api. Bitbucket: Repositories:Read.' },
      { id: 'api_base', label: 'API base URL', placeholder: 'https://api.github.com',
        help: 'Only for self-hosted. GitHub Enterprise Server ends in /api/v3, GitLab self-hosted in /api/v4, Bitbucket Data Center in /rest/api/1.0. Blank uses the cloud endpoint for the selected provider.' },
    ],
  },

  cloudflare: {
    externalIdField: 'account_id',
    note: 'An API token, not the Global API Key — the probe calls GET /user/tokens/verify, which only accepts a token.',
    fields: [
      { id: 'account_id', label: 'Account ID', required: true, placeholder: '023e105f4ecef8ad9ca31a8372d0c353',
        help: 'Cloudflare dashboard → the account overview sidebar.' },
      { id: 'api_token', label: 'API token', kind: 'secret', required: true,
        help: 'Zone:Read and DNS:Read are the required scopes.' },
    ],
  },

  vercel: {
    externalIdField: 'team_id',
    note: 'Probed with GET /v2/user.',
    fields: [
      { id: 'team_id', label: 'Team ID', required: true, placeholder: 'team_xxxxxxxxxxxxxxxx',
        help: 'For a personal account, use the username.' },
      { id: 'api_token', label: 'API token', kind: 'secret', required: true,
        help: 'Vercel → Settings → Tokens. Read-only scope is enough.' },
    ],
  },

  linode: {
    externalIdField: 'account_id',
    note: 'Probed with GET /v4/account.',
    fields: [
      { id: 'account_id', label: 'Account ID or email', required: true },
      { id: 'api_token', label: 'Personal access token', kind: 'secret', required: true,
        help: 'read_only scope across linodes, firewalls, object storage and domains.' },
    ],
  },

  vmware: {
    externalIdField: 'vcenter_host',
    note: 'Probed by creating a vCenter session (POST /rest/com/vmware/cis/session) and deleting it again.',
    fields: [
      { id: 'vcenter_host', label: 'vCenter hostname', required: true, placeholder: 'vcenter.corp.example.com' },
      { id: 'username', label: 'Username', required: true, placeholder: 'svc-cybershield@vsphere.local' },
      { id: 'password', label: 'Password', kind: 'secret', required: true },
      { id: 'verify_ssl', label: 'TLS certificate verification', kind: 'select',
        options: [
          { value: 'true', label: 'Verify (recommended)' },
          { value: 'false', label: 'Do not verify — self-signed vCenter certificate' },
        ],
        help: 'The probe echoes the choice back as tls_verified, so an unverified session is recorded rather than assumed.' },
    ],
  },

  oci: {
    externalIdField: 'tenancy_id',
    note: 'API-key auth. The probe calls the Identity API for the tenancy the key authenticates to, and refuses if that is not the tenancy claimed below.',
    fields: [
      { id: 'tenancy_id', label: 'Tenancy OCID', required: true, placeholder: 'ocid1.tenancy.oc1..aaaa…' },
      { id: 'user_ocid', label: 'User OCID', required: true, placeholder: 'ocid1.user.oc1..aaaa…' },
      { id: 'fingerprint', label: 'API key fingerprint', required: true, placeholder: 'aa:bb:cc:dd:…' },
      { id: 'key_content', label: 'API private key (PEM)', kind: 'textarea', rows: 7, required: true,
        placeholder: '-----BEGIN PRIVATE KEY-----\n…' },
      { id: 'pass_phrase', label: 'Key passphrase', kind: 'secret', help: 'Only if the key is encrypted.' },
      { id: 'region', label: 'Home region', placeholder: 'us-ashburn-1', help: 'Defaults to us-ashburn-1 when blank.' },
    ],
  },

  alibaba: {
    externalIdField: 'account_id',
    note: 'Probed with STS GetCallerIdentity. The account the key belongs to must match the account ID below.',
    fields: [
      { id: 'account_id', label: 'Account ID (UID)', required: true, placeholder: '1234567890123456' },
      { id: 'access_key_id', label: 'AccessKey ID', required: true, placeholder: 'LTAI…' },
      { id: 'access_key_secret', label: 'AccessKey secret', kind: 'secret', required: true },
      { id: 'region', label: 'Region', placeholder: 'cn-hangzhou', help: 'Defaults to cn-hangzhou when blank.' },
    ],
  },
};

/** Fields to render for the current values — `when` resolved, order preserved. */
export function visibleCredFields(
  form: CredentialForm | undefined,
  values: Record<string, string>,
): CredField[] {
  if (!form) return [];
  return form.fields.filter(f => !f.when || f.when(values));
}

/** Required-but-empty labels, so a surface refuses before the probe has to. */
export function missingCredFields(
  form: CredentialForm | undefined,
  values: Record<string, string>,
): string[] {
  return visibleCredFields(form, values)
    .filter(f => f.required && !(values[f.id] || '').trim())
    .map(f => f.label);
}

/**
 * The credential bag to send. `uiOnly` discriminators and blank optionals are
 * dropped — an empty string is not the same as an absent key to a probe that
 * branches on truthiness, and `registry_type: ''` would select the wrong auth
 * shape rather than the default.
 */
export function credentialValues(
  form: CredentialForm | undefined,
  values: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of visibleCredFields(form, values)) {
    if (f.uiOnly) continue;
    const v = (values[f.id] || '').trim();
    if (v) out[f.id] = v;
  }
  return out;
}

/** Seed values for a form: select defaults, then any tile-specific presets. */
export function seedCredForm(form: CredentialForm | undefined, tileId?: string): Record<string, string> {
  if (!form) return {};
  const seeded: Record<string, string> = {};
  for (const f of form.fields) {
    if (f.kind === 'select' && f.options?.length) seeded[f.id] = f.options[0].value;
  }
  Object.assign(seeded, (tileId && form.presetForTile?.[tileId]) || {});
  return seeded;
}

function utf8Base64(text: string): string {
  // btoa is latin1-only; a kubeconfig with a non-ASCII cluster name would throw
  // and lose the credential silently. UTF-8 first, then btoa.
  let bin = '';
  new TextEncoder().encode(text).forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin);
}

/**
 * What `POST /api/v1/cloud-accounts` needs, derived from one form.
 *
 * `create_connector` reads some fields at the TOP LEVEL of the body and stores
 * `credentials` verbatim as `cloud_metadata` (which `/{id}/test` reads back).
 * Both surfaces go through here so the envelope is described once.
 *
 * Two values are re-shaped, and both would otherwise fail AFTER the connector
 * exists — the expensive place to find out:
 *
 *  - `kubeconfig` is BASE64 here and RAW in the pre-create probe body. Same
 *    key, two encodings: test_connection_standalone yaml.safe_load()s it while
 *    test_connector base64-decodes it (matching the kubeconfig_data column mig
 *    543 added). The backend accepts either now; this sends the documented one.
 *  - `verify_ssl` must be a JSON boolean; the string "false" is truthy in
 *    Python and would turn "do not verify" into "verify".
 */
export function buildCreateCredentials(
  form: CredentialForm | undefined,
  values: Record<string, string>,
): { externalId: string; credentials?: Record<string, unknown>; topLevel: Record<string, unknown> } {
  if (!form) return { externalId: '', credentials: undefined, topLevel: {} };
  const out: Record<string, unknown> = credentialValues(form, values);

  if (typeof out.kubeconfig === 'string') {
    try {
      out.kubeconfig = utf8Base64(out.kubeconfig as string);
    } catch {
      // Leave it raw rather than dropping it — the backend's kubeconfig reader
      // accepts raw YAML too, and a dropped credential reads as "no credentials
      // supplied", which is a different and more misleading failure.
    }
  }
  if ('verify_ssl' in out) out.verify_ssl = out.verify_ssl !== 'false';
  if (typeof out.service_account_json === 'string') {
    // Sent parsed when it parses. An unparseable paste is forwarded as-is so
    // the backend reports the parse error rather than the wizard hiding it.
    try { out.service_account_json = JSON.parse(out.service_account_json as string); } catch { /* keep string */ }
  }

  // Fields create reads at the top level of the body as well as (or instead of)
  // inside `credentials` — notably the confused-deputy guard, which checks
  // `data.aws_role_arn or data.role_arn` before it ever looks at credentials.
  const topLevel: Record<string, unknown> = {};
  for (const k of ['aws_access_key_id', 'aws_secret_access_key', 'role_arn', 'aws_external_id']) {
    if (out[k]) topLevel[k] = out[k];
  }
  if (out.role_arn) topLevel.aws_role_arn = out.role_arn;

  return {
    externalId: (values[form.externalIdField] || '').trim(),
    credentials: Object.keys(out).length ? out : undefined,
    topLevel,
  };
}

/**
 * The fields themselves. Presentational: the caller owns the values, the
 * heading and whatever it puts around them, so Settings can wrap this in its
 * connector card and the first-run wizard can render it bare.
 */
export const ProviderCredentialFields: React.FC<{
  form: CredentialForm;
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  /** Tailwind class for inputs, so each surface keeps its own look. */
  inputClass?: string;
}> = ({ form, values, onChange, inputClass }) => {
  const [shown, setShown] = React.useState<Record<string, boolean>>({});
  const box = inputClass
    || 'w-full bg-wiz-dark border border-wiz-border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-wiz-purple font-mono';

  return (
    <>
      {visibleCredFields(form, values).map(f => {
        const value = values[f.id] ?? '';
        return (
          <div key={f.id}>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              {f.label}
              {/* A select always holds a value (it is seeded from its first
                  option), so it can never be "missing" and marking it optional
                  reads as "you may skip this" for what is actually the
                  auth-shape discriminator. */}
              {!f.required && f.kind !== 'select' && (
                <span className="text-gray-600 font-normal"> (optional)</span>
              )}
            </label>
            {f.kind === 'select' ? (
              <select
                value={value || f.options?.[0]?.value || ''}
                onChange={e => onChange(f.id, e.target.value)}
                className="w-full bg-wiz-dark border border-wiz-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-wiz-purple"
              >
                {(f.options || []).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : f.kind === 'textarea' ? (
              <textarea
                value={value}
                onChange={e => onChange(f.id, e.target.value)}
                rows={f.rows ?? 6}
                placeholder={f.placeholder}
                autoComplete="off"
                spellCheck={false}
                className={`${box} text-xs resize-y`}
              />
            ) : (
              <div className="relative">
                <input
                  type={f.kind === 'secret' && !shown[f.id] ? 'password' : 'text'}
                  value={value}
                  onChange={e => onChange(f.id, e.target.value)}
                  placeholder={f.placeholder}
                  autoComplete={f.kind === 'secret' ? 'new-password' : 'off'}
                  spellCheck={false}
                  className={f.kind === 'secret' ? `${box} pr-10` : box}
                />
                {f.kind === 'secret' && (
                  <button
                    type="button"
                    onClick={() => setShown(prev => ({ ...prev, [f.id]: !prev[f.id] }))}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    tabIndex={-1}
                    aria-label={shown[f.id] ? `Hide ${f.label}` : `Show ${f.label}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            {f.help && <p className="mt-1 text-xs text-gray-600">{f.help}</p>}
          </div>
        );
      })}
    </>
  );
};

export default ProviderCredentialFields;
